import { type OrgModelRecord, type OrgModelType, orgModelsService } from "@/app/providers/services";
import { useNotify } from "@/common/notifications";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TYPE_OPTIONS, type GraphExpandDirection } from "../types";
import { getEntityNodeId, getLayoutedGraph, getListData, getRelatedEntityType } from "../utils";

const GRAPH_EXPANSION_LIMIT = 50;

export const useOrgModelGraphExpansion = (selectedRecord: OrgModelRecord | null | undefined, onClear?: () => void) => {
  const notify = useNotify();
  const [graphRecordsById, setGraphRecordsById] = useState<Record<string, OrgModelRecord>>({});
  const [visibleGraphTypes, setVisibleGraphTypes] = useState<OrgModelType[]>(TYPE_OPTIONS);
  const [expandingByNodeId, setExpandingByNodeId] = useState<
    Record<string, { incoming: boolean; outgoing: boolean }>
  >({});
  const previousSelectedRecordId = useRef<string | null>(null);

  useEffect(() => {
    const selectedRecordId = selectedRecord?.id || null;
    if (previousSelectedRecordId.current !== selectedRecordId) {
      previousSelectedRecordId.current = selectedRecordId;
      setGraphRecordsById(selectedRecordId && selectedRecord ? { [selectedRecordId]: selectedRecord } : {});
      setExpandingByNodeId({});
      return;
    }

    if (!selectedRecordId || !selectedRecord) return;
    setGraphRecordsById((prev) => {
      if (!prev[selectedRecordId]) return prev;
      return {
        ...prev,
        [selectedRecordId]: selectedRecord,
      };
    });
  }, [selectedRecord]);

  const clearGraph = () => {
    previousSelectedRecordId.current = null;
    setGraphRecordsById({});
    setExpandingByNodeId({});
    onClear?.();
  };

  const setNodeExpanding = (nodeId: string, direction: GraphExpandDirection, value: boolean) => {
    setExpandingByNodeId((prev) => {
      const current = prev[nodeId] || { incoming: false, outgoing: false };
      return {
        ...prev,
        [nodeId]: {
          ...current,
          [direction]: value,
        },
      };
    });
  };

  const fetchConnectedRecords = useCallback(
    async (entityType: string, entityId: string, direction: GraphExpandDirection) => {
      const outgoingFilter = {
        subjectType: entityType,
        subjectId: entityId,
        $limit: GRAPH_EXPANSION_LIMIT,
      };
      const incomingFilter = {
        relatedId: entityId,
        $limit: GRAPH_EXPANSION_LIMIT,
      };
      const response = await (orgModelsService as any).getList(
        direction === "outgoing" ? outgoingFilter : incomingFilter,
      );
      const total = typeof response?.total === "number" ? response.total : undefined;
      const records = getListData(response).filter((item) => Boolean(item.id));
      const filteredRecords =
        direction === "outgoing" ? records : records.filter((item) => getRelatedEntityType(item) === entityType);
      return {
        total,
        records: filteredRecords,
      };
    },
    [],
  );

  const expandFromNode = useCallback(
    async (entityType: string, entityId: string, direction: GraphExpandDirection) => {
      const nodeId = getEntityNodeId(entityType, entityId);
      setNodeExpanding(nodeId, direction, true);
      try {
        const { records: connected, total } = await fetchConnectedRecords(entityType, entityId, direction);
        if (connected.length === 0) {
          notify.info("No additional records found.");
          return;
        }
        if (typeof total === "number" && total > GRAPH_EXPANSION_LIMIT) {
          notify.info(`Only the first ${GRAPH_EXPANSION_LIMIT} matching records were returned (${total} total).`);
        }

        setGraphRecordsById((prev) => {
          const next = { ...prev };
          for (const item of connected) {
            if (!item.id) continue;
            next[item.id] = item;
          }
          return next;
        });
      } catch (error: any) {
        notify.error(error?.message || "Failed to expand graph.");
      } finally {
        setNodeExpanding(nodeId, direction, false);
      }
    },
    [fetchConnectedRecords, notify],
  );

  const onExpandOutgoing = useCallback(
    (entityType: string, entityId: string) => {
      void expandFromNode(entityType, entityId, "outgoing");
    },
    [expandFromNode],
  );

  const onExpandIncoming = useCallback(
    (entityType: string, entityId: string) => {
      void expandFromNode(entityType, entityId, "incoming");
    },
    [expandFromNode],
  );

  const setGraphTypeVisible = useCallback((type: OrgModelType, visible: boolean) => {
    setVisibleGraphTypes((prev) => {
      if (visible) {
        return prev.includes(type) ? prev : [...prev, type];
      }
      return prev.filter((item) => item !== type);
    });
  }, []);

  const layoutedGraph = useMemo(
    () => getLayoutedGraph(graphRecordsById, visibleGraphTypes, expandingByNodeId, onExpandIncoming, onExpandOutgoing),
    [graphRecordsById, visibleGraphTypes, expandingByNodeId, onExpandIncoming, onExpandOutgoing],
  );

  const hasGraphRecords = Object.keys(graphRecordsById).length > 0;
  const hasGraph = layoutedGraph.nodes.length > 0;

  return {
    visibleGraphTypes,
    setGraphTypeVisible,
    layoutedGraph,
    hasGraphRecords,
    hasGraph,
    clearGraph,
  };
};
